import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ETFs.css';

function ETFs() {
  const [etfs, setEtfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterPolicy, setFilterPolicy] = useState('');

  useEffect(() => {
    fetchETFs();
  }, []);

  const fetchETFs = async () => {
    try {
      const response = await axios.get('/etfs.json');
      setEtfs(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load ETF data');
      setLoading(false);
    }
  };

  const uniqueRegions = [...new Set(etfs.map(etf => etf.region).filter(Boolean))].sort();
  const uniquePolicies = [...new Set(etfs.map(etf => etf.dividend_policy).filter(Boolean))].sort();

  const filteredETFs = etfs.filter(etf => {
    const matchesSearch = !searchTerm || 
      etf.ticker.toLowerCase().includes(searchTerm.toLowerCase()) ||
      etf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (etf.description && etf.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRegion = !filterRegion || etf.region === filterRegion;
    const matchesPolicy = !filterPolicy || etf.dividend_policy === filterPolicy;
    
    return matchesSearch && matchesRegion && matchesPolicy;
  });

  if (loading) {
    return <div className="etfs-container"><div className="loading">Loading ETF data...</div></div>;
  }

  if (error) {
    return <div className="etfs-container"><div className="error">{error}</div></div>;
  }

  return (
    <div className="etfs-container">
      <div className="etfs-header">
        <h2>ETF Documentation</h2>
        <p>Comprehensive information about available ETFs</p>
      </div>

      <div className="etfs-filters">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by ticker, name, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Region:</label>
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            className="filter-select"
          >
            <option value="">All Regions</option>
            {uniqueRegions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Dividend Policy:</label>
          <select
            value={filterPolicy}
            onChange={(e) => setFilterPolicy(e.target.value)}
            className="filter-select"
          >
            <option value="">All Policies</option>
            {uniquePolicies.map(policy => (
              <option key={policy} value={policy}>{policy}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="etfs-stats">
        <span>Total ETFs: <strong>{etfs.length}</strong></span>
        <span>Showing: <strong>{filteredETFs.length}</strong></span>
      </div>

      <div className="etfs-grid">
        {filteredETFs.map((etf, index) => (
          <div key={index} className="etf-card">
            <div className="etf-header">
              <h3>{etf.ticker}</h3>
              <span className="etf-name">{etf.name}</span>
            </div>
            
            <div className="etf-details">
              {etf.isin && (
                <div className="etf-detail">
                  <span className="detail-label">ISIN:</span>
                  <span className="detail-value">{etf.isin}</span>
                </div>
              )}
              
              {etf.index && (
                <div className="etf-detail">
                  <span className="detail-label">Index:</span>
                  <span className="detail-value">{etf.index}</span>
                </div>
              )}
              
              {etf.region && (
                <div className="etf-detail">
                  <span className="detail-label">Region:</span>
                  <span className="detail-value">{etf.region}</span>
                </div>
              )}
              
              {etf.expense_ratio && (
                <div className="etf-detail">
                  <span className="detail-label">Expense Ratio:</span>
                  <span className="detail-value">{etf.expense_ratio}</span>
                </div>
              )}
              
              {etf.dividend_policy && (
                <div className="etf-detail">
                  <span className="detail-label">Dividend Policy:</span>
                  <span className={`detail-value policy-${etf.dividend_policy.toLowerCase()}`}>
                    {etf.dividend_policy}
                  </span>
                </div>
              )}
              
              {etf.replication_method && (
                <div className="etf-detail">
                  <span className="detail-label">Replication:</span>
                  <span className="detail-value">{etf.replication_method}</span>
                </div>
              )}
            </div>
            
            {etf.description && (
              <div className="etf-description">
                <p>{etf.description}</p>
              </div>
            )}
            
            {etf.notes && (
              <div className="etf-notes">
                <strong>Notes:</strong> {etf.notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredETFs.length === 0 && (
        <div className="no-results">
          <p>No ETFs found matching your criteria.</p>
        </div>
      )}
    </div>
  );
}

export default ETFs;

